import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { RollingType } from 'types/GlobalKpiOption'
import { Action } from 'types/GlobalUserEvents'
import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'
import { ShowPerEnum, isCustomEconomyMetric } from 'types/GlobalMetric'

import Banner from 'components_new/molecules/Banner'

import MetricFooter from 'components_new/organisms/MetricFooter'
import MetricView from 'components_new/organisms/MetricView'

import MetricTemplate from 'components_new/templates/MetricTemplate'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useMetric } from 'redux/hooks/Metrics'
import { useMetricQueryParams } from 'utils/useMetricQueryParams'
import { useParams } from 'utils/hooks'
import { useQueryParamState } from 'utils/queryHandler'
import { useTranslation } from 'language/translator'

import { getContext } from 'utils/userEvents'

import Locked from './locked'
import { useAllowAccessMetric } from 'redux/hooks/Permissions'

const EMPTY_SLICES: string[] = []

const Metric = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const searchParams = new URLSearchParams(search)

  const metric = useMetric(id, null)
  const { isCompanyBased } = useAccountSettings()

  const [queryParams, setQueryParams] = useMetricQueryParams()

  const sliceIds = metric?.slices.available.map((slice) => slice.id) ?? []
  const variantIds = isCustomEconomyMetric(metric)
    ? metric.variants.map((item) => item.id)
    : []

  // query states
  const [chart, setChart] = useQueryParamState(
    'chart',
    'line',
    Object.values(['bar', 'line'])
  )

  const [distribution, setDistribution] = useQueryParamState(
    'distribution',
    null,
    Object.values(ShowPerEnum)
  )
  const [rollingSum, setRollingSum] = useQueryParamState(
    'rolling_sum',
    null,
    Object.values(RollingType)
  )
  const [slices, setSlices] = useQueryParamState(
    'slices',
    EMPTY_SLICES,
    sliceIds
  )
  const [variant, setVariant] = useQueryParamState(
    'variant',
    isCustomEconomyMetric(metric) ? metric.default_variant : null,
    variantIds
  )
  const [segmentation, setSegmentation] = useQueryParamState<string | null>(
    'segment_by',
    null
  )

  useEffect(() => {
    setChart(segmentation ? 'bar' : 'line')
  }, [segmentation])

  // set default period on mount if none is set
  useEffect(() => {
    if (!queryParams.period_filter && !queryParams.from_date) {
      setQueryParams({
        ...queryParams,
        period_filter: PeriodFilter.LAST_12_MONTHS_TO_TODAY
      })
    }
  }, [])

  const settings = useMemo(
    () => ({
      slices: slices,
      variant_kpi_template_id: variant,
      distribution: distribution,
      rolling_sum: rollingSum
    }),
    [slices, distribution, rollingSum, variant]
  )

  useEffect(() => {
    // set default variant for custom economy metrics
    if (
      metric &&
      isCustomEconomyMetric(metric) &&
      metric.variants.length > 0 &&
      !variant
    ) {
      setVariant(metric.default_variant)
    }
  }, [metric?.id])

  useEffect(() => {
    if (id) {
      const context = getContext(searchParams)

      UserEvents.log({
        action: Action.METRIC_OPENED,
        context: context,
        dashboard_id: null,
        dashboard_group_id: null
      })
    }
  }, [id])

  const loading = !metric
  const locked = !useAllowAccessMetric(id)

  let period: PeriodFilter | CustomPeriodFilter | null =
    queryParams.period_filter ?? null

  if (queryParams.from_date && queryParams.to_date) {
    period = {
      from: queryParams.from_date,
      to: queryParams.to_date
    }
  }

  return (
    <MetricTemplate
      footer={{
        content: (
          <MetricFooter
            chart={chart}
            id={id}
            onChangeChart={setChart}
            onChangePeriod={(value) => {
              if (value && typeof value === 'object') {
                setQueryParams({
                  ...queryParams,
                  from_date: value.from ?? undefined,
                  period_filter: undefined,
                  to_date: value.to ?? undefined
                })
              } else {
                setQueryParams({
                  ...queryParams,
                  from_date: undefined,
                  period_filter: value ?? undefined,
                  to_date: undefined
                })
              }
            }}
            onChangeSegmentation={setSegmentation}
            period={period}
            segmentation={segmentation}
            settings={settings}
            onChangeDistribution={setDistribution}
            onChangeRollingSum={setRollingSum}
            onChangeSlices={setSlices}
            onChangeVariant={setVariant}
            onChangeRealEstates={
              isCompanyBased
                ? undefined
                : (values) =>
                    setQueryParams({
                      ...queryParams,
                      real_estates: values.length > 0 ? values : undefined
                    })
            }
            selectedRealEstates={queryParams.real_estates ?? []}
          />
        ),
        height: 56
      }}
      toolbar={{
        collapsed: slices.length <= 1,
        content: (
          <Banner
            body={t('entities', 'multiple_slices_warning_body')}
            title={t('global', 'note')}
          />
        ),
        disablePadding: true
      }}
      variant="static"
    >
      {!loading && locked ? (
        <Locked />
      ) : (
        <MetricView
          chart={chart}
          clickOptions={{
            breakdownBy: true,
            explore: true
          }}
          id={id}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          settings={settings}
        />
      )}
    </MetricTemplate>
  )
}

export default Metric
